import { Button, Icon, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { NON_EDITABLE_AUTOMATION_STATUS, PAYMENT_METHOD_TYPES } from '@hero/constants';
import { BasicContactTable, ContactsEditModal } from '@hero/organisms';
import { useUpdateAutomation } from '@hero/services';
import { useNotification } from '@hero/store/hooks';
import { removeRecipientSchema } from '@hero/validations';

import * as S from './style';

function AutomationContactRecipients({ automation }) {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { mutateAsync: updateAutomation } = useUpdateAutomation();

  const [isContactsEditModalOpen, setIsContactsEditModalOpen] = useState(false);

  const isEditable = React.useMemo(
    () => !NON_EDITABLE_AUTOMATION_STATUS.includes(automation?.status),
    [automation]
  );

  const updateContacts = async contactIds => {
    await updateAutomation({
      params: { code: automation.code },
      body: {
        contacts: {
          ids: contactIds
        }
      },
      successMessage: t('recipientsUpdatedSuccessfully')
    });
  };

  const onDeleteModal = (recipientId, ids) => {
    showNotification({
      title: t('areYouSureRemoveRecipient'),
      message: (
        <Trans i18nKey="areYouSureRemoveAutomationContactMessage" components={{ b: <b /> }} />
      ),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      formData: [
        {
          name: 'confirm',
          title: (
            <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
              {t('removeRecipientModalConfirmLabel')}
            </Typography>
          ),
          type: 'text'
        }
      ],
      formValidationSchema: removeRecipientSchema,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          await updateAutomation({
            params: { code: automation.code },
            body: {
              contacts: {
                ids: ids.filter(item => item !== recipientId)
              }
            },
            successMessage: t('recipientRemovedSuccessfully')
          });
        }
      }
    });
  };

  return (
    <S.RecipientsWrapper>
      <S.RecipientsHeader>
        <Typography variant="body2">
          <Trans
            i18nKey="totalRecipients"
            values={{ count: automation?.contacts?.ids?.length || 0 }}
            components={{ b: <b /> }}
          />
        </Typography>
        <VerticalDivider $height={24} $margin="0 10px" />
        <Button
          variant="link"
          disabled={!isEditable}
          onClick={() => {
            setIsContactsEditModalOpen(true);
          }}
        >
          <Icon name="add" color="var(--color-accent-primary-default)" />
          {t('addRecipients')}
        </Button>
      </S.RecipientsHeader>
      <BasicContactTable
        ids={automation?.contacts?.ids}
        disabledColumns={[automation?.type === 'birthday' ? 'anniversary' : 'birthday']}
        actionButtons={{
          delete: {
            onClick: onDeleteModal
          }
        }}
      />

      {automation && isContactsEditModalOpen && (
        <ContactsEditModal
          onNextReviewAndConfirmStep={({ onNext }) => {
            onNext();
          }}
          isModalOpen={isContactsEditModalOpen}
          onRequestClose={() => setIsContactsEditModalOpen(false)}
          updateContacts={updateContacts}
          selectedContactIds={automation?.contacts?.ids || []}
          isCCPayment={automation?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard}
          disabledColumns={[automation?.type === 'birthday' ? 'anniversary' : 'birthday']}
        />
      )}
    </S.RecipientsWrapper>
  );
}

export { AutomationContactRecipients };
